import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  FlaskConical, Lightbulb, Microscope, TestTube, Rocket, CheckCircle2, 
  Plus, Search, Calendar, Target
} from "lucide-react";
import { toast } from "sonner";
import { API } from "@/config";

const STAGES = [
  { id: "idea", name: "Idea", icon: Lightbulb, color: "bg-yellow-100 text-yellow-700" },
  { id: "concept", name: "Concept", icon: FlaskConical, color: "bg-blue-100 text-blue-700" },
  { id: "development", name: "Development", icon: Microscope, color: "bg-purple-100 text-purple-700" },
  { id: "testing", name: "Testing", icon: TestTube, color: "bg-orange-100 text-orange-700" },
  { id: "commercialization", name: "Commercialization", icon: Rocket, color: "bg-indigo-100 text-indigo-700" },
  { id: "launched", name: "Launched", icon: CheckCircle2, color: "bg-green-100 text-green-700" }
];

const MOCK_PROJECTS = [
  { id: "proj-001", name: "Organic Breakfast Line", description: "New line of organic breakfast items", stage: "development", item_count: 3, created_at: "2024-10-15" },
  { id: "proj-002", name: "Plant-Based Burritos", description: "Vegan-friendly burrito options", stage: "testing", item_count: 2, created_at: "2024-09-01" },
  { id: "proj-003", name: "Kids Meal Program", description: "Nutritious meals for school programs", stage: "concept", item_count: 4, created_at: "2024-11-20" },
  { id: "proj-004", name: "Premium Protein Bowl", description: "High-protein bowl with premium ingredients", stage: "idea", item_count: 1, created_at: "2025-01-05" },
  { id: "proj-005", name: "Gluten-Free Wraps", description: "Gluten-free tortilla wraps", stage: "commercialization", item_count: 2, created_at: "2024-06-15" },
  { id: "proj-006", name: "Mediterranean Bowl", description: "Mediterranean-inspired grain bowl", stage: "launched", item_count: 1, created_at: "2024-03-01" }
];

const RDProjectsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [stageFilter, setStageFilter] = useState(searchParams.get('stage') || "all");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newProject, setNewProject] = useState({
    name: "",
    description: "",
    stage: "idea",
    opportunity_id: null,
    opportunity_name: null
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [projectsRes, oppsRes] = await Promise.all([
        axios.get(`${API}/rd/projects`),
        axios.get(`${API}/crm/opportunities`)
      ]);
      setProjects(projectsRes.data);
      setOpportunities(oppsRes.data);
    } catch (error) {
      console.error('Failed to fetch data', error);
      setProjects(MOCK_PROJECTS);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/rd/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects', error);
      setProjects(MOCK_PROJECTS);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async () => {
    try {
      const response = await axios.post(`${API}/rd/projects`, newProject);
      toast.success("Project created");
      setShowCreateModal(false);
      setNewProject({ name: "", description: "", stage: "idea", opportunity_id: null, opportunity_name: null });
      navigate(`/rd/projects/${response.data.id}`);
    } catch (error) {
      console.error('Failed to create project', error);
      // Add locally for demo
      const newProj = {
        ...newProject,
        id: `proj-${Date.now()}`,
        item_count: 0,
        created_at: new Date().toISOString().split('T')[0]
      };
      setProjects(prev => [newProj, ...prev]);
      toast.success("Project created");
      setShowCreateModal(false);
      setNewProject({ name: "", description: "", stage: "idea", opportunity_id: null, opportunity_name: null });
      navigate(`/rd/projects/${newProj.id}`);
    }
  };

  const handleOpportunitySelect = (oppId) => {
    if (oppId === "none") {
      setNewProject({ ...newProject, opportunity_id: null, opportunity_name: null });
    } else {
      const opp = opportunities.find(o => o.id === oppId);
      setNewProject({ 
        ...newProject, 
        opportunity_id: oppId, 
        opportunity_name: opp?.customer_name || null 
      });
    }
  };

  const getStageBadge = (stageId) => {
    const stage = STAGES.find(s => s.id === stageId);
    if (!stage) return null;
    return <Badge className={stage.color}>{stage.name}</Badge>;
  };

  const getStageIcon = (stageId) => {
    const stage = STAGES.find(s => s.id === stageId);
    if (!stage) return null;
    const Icon = stage.icon;
    return <Icon className={`w-5 h-5 ${stage.color.split(' ')[1]}`} />;
  };

  const filteredProjects = projects.filter(project => {
    const matchesSearch = searchQuery === "" || 
      project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStage = stageFilter === "all" || project.stage === stageFilter;
    return matchesSearch && matchesStage;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <FlaskConical className="w-8 h-8" />
            R&D Projects
          </h1>
          <p className="text-gray-600">Manage all R&D projects</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="w-4 h-4 mr-2" />
          New Project
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex gap-4">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search projects..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <Select value={stageFilter} onValueChange={setStageFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filter by stage" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Stages</SelectItem>
              {STAGES.filter((s) => s.id != null && String(s.id) !== "").map(stage => (
                <SelectItem key={stage.id} value={String(stage.id)}>{stage.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Projects Grid */}
      <div className="grid grid-cols-3 gap-4">
        {filteredProjects.length === 0 ? (
          <div className="col-span-3 text-center py-12 text-gray-500">
            No projects found
          </div>
        ) : (
          filteredProjects.map(project => (
            <Card 
              key={project.id}
              className="cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => navigate(`/rd/projects/${project.id}`)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                    {getStageIcon(project.stage)}
                  </div>
                  {getStageBadge(project.stage)}
                </div>
                <h3 className="font-semibold text-lg mb-1">{project.name}</h3>
                <p className="text-sm text-gray-500 mb-3 line-clamp-2">{project.description}</p>
                {project.opportunity_name && (
                  <div className="mb-3">
                    <Badge variant="outline" className="text-xs bg-blue-50 border-blue-200 text-blue-700">
                      <Target className="w-3 h-3 mr-1" />
                      {project.opportunity_name}
                    </Badge>
                  </div>
                )}
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <FlaskConical className="w-3 h-3" />
                    {project.item_count} items
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {new Date(project.created_at).toLocaleDateString()}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create Project Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Project</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Project Name *</Label>
              <Input
                value={newProject.name}
                onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                placeholder="Enter project name"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={newProject.description}
                onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                placeholder="Describe the project..."
              />
            </div>
            <div>
              <Label>Link to CRM Opportunity</Label>
              <Select 
                value={newProject.opportunity_id || "none"} 
                onValueChange={handleOpportunitySelect}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select an opportunity (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No linked opportunity</SelectItem>
                  {opportunities
                    .filter((opp) => opp.id != null && String(opp.id) !== "")
                    .map(opp => (
                      <SelectItem key={opp.id} value={String(opp.id)}>
                      <div className="flex items-center gap-2">
                        <Target className="w-3 h-3 text-blue-600" />
                        {opp.customer_name} - {opp.description?.substring(0, 30)}...
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">Link this project to a CRM opportunity for tracking</p>
            </div>
            <div>
              <Label>Initial Stage</Label>
              <Select value={newProject.stage} onValueChange={(val) => setNewProject({ ...newProject, stage: val })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STAGES.filter((s) => s.id != null && String(s.id) !== "").map(stage => (
                    <SelectItem key={stage.id} value={String(stage.id)}>{stage.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button onClick={handleCreateProject} disabled={!newProject.name}>Create Project</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RDProjectsPage;
